// CollegeProgress.jsx
import React from 'react';
import { Bar, Pie, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
} from 'chart.js';
import { Link } from 'react-router-dom';
import CollegeHeader from './CollegeHeader';
import CollegeHomeSideBar from './CollegeHomeSideBar';
import { BarChart3, Users, TrendingUp, Layers } from 'lucide-react';

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  ArcElement,
  PointElement,
  LineElement,
);

// --- Design Tokens ------------------------------------------------------------

const T = {
  border:  '1px solid var(--border-subtle, #E2E8F0)',
  radius:  { sm: '8px', md: '12px', lg: '16px', xl: '20px', xxl: '24px', full: '999px' },
  shadow:  {
    xs: '0 1px 3px rgba(0,0,0,0.05)',
    sm: '0 2px 8px rgba(0,0,0,0.07)',
    md: '0 4px 20px rgba(0,0,0,0.08)',
  },
  brand:  'var(--brand-primary, var(--primary-blue, #4F46E5))',
  text: {
    heading:  'var(--text-heading,  #0F172A)',
    body:     'var(--text-body,     #334155)',
    muted:    'var(--text-muted,    #64748B)',
    disabled: 'var(--text-disabled, #94A3B8)',
  },
  bg: { main: 'var(--bg-main, #F8FAFC)', section: 'var(--bg-section, #FFFFFF)' },
};

// --- Static student data (unchanged) -----------------------------------------

const students = [
  { name: 'Eleanor Pena',  email: 'eleanor.pena@example.com',  branch: 'Computer Science', technology: 'React',   level: 3  },
  { name: 'Jessia Rose',   email: 'jessia.rose@example.com',   branch: 'Information Tech',  technology: 'Node.js', level: 8  },
  { name: 'Guy Hawkins',   email: 'guy.hawkins@example.com',   branch: 'Electronics',       technology: 'Python',  level: 14 },
  { name: 'Jacob Jones',   email: 'jacob.jones@example.com',   branch: 'Computer Science',  technology: 'React',   level: 18 },
];

// --- Derived data (unchanged) -------------------------------------------------

const techCounts = students.reduce((acc, curr) => {
  acc[curr.technology] = (acc[curr.technology] || 0) + 1;
  return acc;
}, {});

const branchCounts = students.reduce((acc, curr) => {
  acc[curr.branch] = (acc[curr.branch] || 0) + 1;
  return acc;
}, {});

const levelRanges = [
  { label: '1–5',   min: 1,  max: 5  },
  { label: '6–10',  min: 6,  max: 10 },
  { label: '11–15', min: 11, max: 15 },
  { label: '16–20', min: 16, max: 20 },
];

const levelCounts = levelRanges.reduce((acc, range) => {
  const count = students.filter((s) => s.level >= range.min && s.level <= range.max).length;
  acc[range.label] = count;
  return acc;
}, {});

const averageLevel =
  students.reduce((sum, s) => sum + s.level, 0) / students.length;

// --- Summary stat tiles --------------------------------------------------------

const STATS = [
  {
    icon:    <Users    style={{ width: 18, height: 18 }} />,
    label:   'Total Students',
    value:   students.length,
    accent:  'var(--primary-blue, #4F46E5)',
    accentBg:'rgba(79, 70, 229,0.08)',
    stripColor: 'var(--primary-blue, #4F46E5)',
  },
  {
    icon:    <TrendingUp style={{ width: 18, height: 18 }} />,
    label:   'Average Level',
    value:   averageLevel.toFixed(2),
    accent:  '#7C3AED',
    accentBg:'rgba(124,58,237,0.08)',
    stripColor: '#7C3AED',
  },
  {
    icon:    <BarChart3 style={{ width: 18, height: 18 }} />,
    label:   'Technologies',
    value:   Object.keys(techCounts).length,
    accent:  '#059669',
    accentBg:'rgba(5,150,105,0.08)',
    stripColor: '#059669',
  },
  {
    icon:    <Layers style={{ width: 18, height: 18 }} />,
    label:   'Departments',
    value:   Object.keys(branchCounts).length,
    accent:  '#D97706',
    accentBg:'rgba(217,119,6,0.08)',
    stripColor: '#D97706',
  },
];

// --- Shared chart card wrapper ------------------------------------------------

const ChartCard = ({ title, stripColor, children }) => (
  <div style={{
    background: '#FFFFFF', border: T.border,
    borderRadius: T.radius.xl, overflow: 'hidden',
    boxShadow: T.shadow.sm,
    display: 'flex', flexDirection: 'column',
  }}>
    <div style={{ height: '4px', background: stripColor, flexShrink: 0 }} />
    <div style={{ padding: '22px 24px 24px', flex: 1 }}>
      <p style={{
        fontSize: '11px', fontWeight: 700, color: T.text.muted,
        textTransform: 'uppercase', letterSpacing: '0.07em',
        margin: '0 0 18px',
      }}>
        {title}
      </p>
      <div style={{ height: '300px', position: 'relative' }}>
        {children}
      </div>
    </div>
  </div>
);

// --- Shared chart options helpers ---------------------------------------------

const baseScales = {
  x: {
    ticks: { color: '#64748B', font: { weight: '600', size: 11 } },
    grid:  { display: false },
    border: { display: false },
  },
  y: {
    ticks: { color: '#64748B', font: { size: 11 } },
    grid:  { color: 'rgba(226,232,240,0.8)', drawBorder: false },
    border: { display: false },
    beginAtZero: true,
  },
};

const baseOptions = {
  maintainAspectRatio: false,
  responsive: true,
  plugins: {
    legend: { display: false },
    tooltip: {
      backgroundColor: '#0F172A',
      titleColor: '#F1F5F9',
      bodyColor: '#CBD5E1',
      padding: 10,
      cornerRadius: 8,
      titleFont: { weight: '700', size: 12 },
    },
  },
};

// --- CollegeProgress ----------------------------------------------------------

const CollegeProgress = () => (
  <div className="college-main-layout" style={{ background: T.bg.main, fontFamily: 'inherit' }}>
    <CollegeHeader />

    <div className="college-content-wrapper">
      <CollegeHomeSideBar />

      <main className="college-main-content">
        <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '36px 28px 72px' }}>

          {/* -- Page Header ------------------------------------------------ */}
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', gap: '16px',
            marginBottom: '28px', flexWrap: 'wrap',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '48px', height: '48px', borderRadius: T.radius.lg,
                background: 'rgba(79, 70, 229,0.08)', border: '1px solid rgba(79, 70, 229,0.15)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <BarChart3 style={{ width: '22px', height: '22px', color: T.brand }} />
              </div>
              <div>
                <h1 style={{
                  fontSize: '22px', fontWeight: 800, margin: 0,
                  color: T.text.heading, letterSpacing: '-0.02em', lineHeight: 1.2,
                }}>
                  Institution Insights
                </h1>
                <p style={{ fontSize: '13px', color: T.text.muted, margin: '4px 0 0', fontWeight: 500 }}>
                  Overall skill performance, technology distribution &amp; level analytics
                </p>
              </div>
            </div>

            <Link
              to="/CollegeDashboard"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 18px', borderRadius: T.radius.md,
                background: '#FFFFFF', border: T.border,
                boxShadow: T.shadow.xs, fontSize: '12px', fontWeight: 600,
                color: T.text.muted, textDecoration: 'none', transition: 'all 0.15s',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brand; e.currentTarget.style.color = T.brand; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border-subtle,#E2E8F0)'; e.currentTarget.style.color = T.text.muted; }}
            >
              ← Back to Dashboard
            </Link>
          </div>

          {/* -- Summary Stats Row ------------------------------------------- */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: '14px', marginBottom: '24px',
          }}>
            {STATS.map((stat) => (
              <div key={stat.label} style={{
                background: '#FFFFFF', border: T.border,
                borderRadius: T.radius.xl, overflow: 'hidden',
                boxShadow: T.shadow.xs,
              }}>
                <div style={{ height: '3px', background: stat.stripColor }} />
                <div style={{ padding: '16px 18px', display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <div style={{
                    width: '38px', height: '38px', borderRadius: T.radius.md,
                    background: stat.accentBg, color: stat.accent,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    {stat.icon}
                  </div>
                  <div>
                    <p style={{
                      fontSize: '10px', fontWeight: 700, color: T.text.muted,
                      textTransform: 'uppercase', letterSpacing: '0.06em', margin: '0 0 3px',
                    }}>
                      {stat.label}
                    </p>
                    <p style={{
                      fontSize: '22px', fontWeight: 900, color: stat.accent,
                      margin: 0, lineHeight: 1, letterSpacing: '-0.02em',
                    }}>
                      {stat.value}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* -- Charts Grid ------------------------------------------------- */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(440px, 1fr))',
            gap: '20px',
          }}>

            {/* Bar - Students by Technology */}
            <ChartCard title="Students by Technology" stripColor="linear-gradient(90deg,var(--primary-blue, #4F46E5),#7C3AED)">
              <Bar
                data={{
                  labels: Object.keys(techCounts),
                  datasets: [{
                    label: 'Students',
                    data: Object.values(techCounts),
                    backgroundColor: ['var(--primary-blue, #4F46E5)', '#7C3AED', '#0891B2'],
                    hoverBackgroundColor: ['var(--secondary-blue, #1E3A8A)', '#6D28D9', '#0E7490'],
                    borderRadius: 8,
                    borderSkipped: false,
                  }],
                }}
                options={{
                  ...baseOptions,
                  scales: baseScales,
                }}
              />
            </ChartCard>

            {/* Pie - Departmental Split */}
            <ChartCard title="Departmental Split" stripColor="linear-gradient(90deg,#059669,#0D9488)">
              <Pie
                data={{
                  labels: Object.keys(branchCounts),
                  datasets: [{
                    data: Object.values(branchCounts),
                    backgroundColor: ['var(--primary-blue, #4F46E5)', '#059669', '#D97706'],
                    hoverBackgroundColor: ['var(--secondary-blue, #1E3A8A)', '#047857', '#B45309'],
                    borderWidth: 2,
                    borderColor: '#FFFFFF',
                  }],
                }}
                options={{
                  ...baseOptions,
                  plugins: {
                    ...baseOptions.plugins,
                    legend: {
                      position: 'bottom',
                      labels: {
                        color: '#64748B',
                        padding: 20,
                        font: { weight: '600', size: 12 },
                        usePointStyle: true,
                        pointStyleWidth: 10,
                      },
                    },
                  },
                }}
              />
            </ChartCard>

            {/* Line - Skill Level Distribution */}
            <ChartCard title="Skill Level Distribution" stripColor="linear-gradient(90deg,#7C3AED,#0891B2)">
              <Line
                data={{
                  labels: Object.keys(levelCounts),
                  datasets: [{
                    label: 'Students',
                    data: Object.values(levelCounts),
                    borderColor: 'var(--primary-blue, #4F46E5)',
                    backgroundColor: 'rgba(79, 70, 229,0.08)',
                    tension: 0.4,
                    fill: true,
                    pointRadius: 6,
                    pointBackgroundColor: 'var(--primary-blue, #4F46E5)',
                    pointBorderColor: '#FFFFFF',
                    pointBorderWidth: 2,
                    pointHoverRadius: 8,
                  }],
                }}
                options={{
                  ...baseOptions,
                  scales: baseScales,
                }}
              />
            </ChartCard>

          </div>

          {/* -- Students Summary Table -------------------------------------- */}
          <div style={{
            background: '#FFFFFF', border: T.border,
            borderRadius: T.radius.xl, overflow: 'hidden',
            boxShadow: T.shadow.sm, marginTop: '20px',
          }}>
            <div style={{ padding: '18px 24px', borderBottom: T.border }}>
              <p style={{
                fontSize: '11px', fontWeight: 700, color: T.text.muted,
                textTransform: 'uppercase', letterSpacing: '0.07em', margin: 0,
              }}>
                Student Overview
              </p>
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: T.bg.main }}>
                    {['Student', 'Branch', 'Technology', 'Skill Level'].map((h) => (
                      <th key={h} style={{
                        padding: '11px 18px', textAlign: 'left',
                        fontSize: '11px', fontWeight: 700, color: T.brand,
                        textTransform: 'uppercase', letterSpacing: '0.05em',
                        borderBottom: T.border, whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {students.map((s, idx) => {
                    const levelPct = Math.round((s.level / 20) * 100);
                    const levelColor = s.level >= 16 ? '#16A34A' : s.level >= 11 ? 'var(--primary-blue, #4F46E5)' : s.level >= 6 ? '#D97706' : '#64748B';
                    const levelBg   = s.level >= 16 ? '#DCFCE7' : s.level >= 11 ? '#DBEAFE' : s.level >= 6 ? '#FEF3C7' : '#F1F5F9';
                    return (
                      <tr
                        key={idx}
                        style={{ borderBottom: T.border, transition: 'background 0.12s' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = T.bg.main; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = '#FFFFFF'; }}
                      >
                        <td style={{ padding: '13px 18px' }}>
                          <div style={{ fontWeight: 600, fontSize: '13px', color: T.text.heading }}>{s.name}</div>
                          <div style={{ fontSize: '11px', color: T.text.muted, marginTop: '2px' }}>{s.email}</div>
                        </td>
                        <td style={{ padding: '13px 18px' }}>
                          <span style={{
                            fontSize: '12px', fontWeight: 600, color: T.text.body,
                            background: T.bg.main, border: T.border,
                            padding: '3px 10px', borderRadius: T.radius.full,
                          }}>
                            {s.branch}
                          </span>
                        </td>
                        <td style={{ padding: '13px 18px' }}>
                          <span style={{
                            fontSize: '12px', fontWeight: 700,
                            color: 'var(--primary-blue, #4F46E5)', background: 'rgba(79, 70, 229,0.07)',
                            border: '1px solid rgba(79, 70, 229,0.18)',
                            padding: '3px 10px', borderRadius: T.radius.full,
                          }}>
                            {s.technology}
                          </span>
                        </td>
                        <td style={{ padding: '13px 18px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              flex: 1, height: '5px', background: '#E2E8F0',
                              borderRadius: T.radius.full, overflow: 'hidden', maxWidth: '80px',
                            }}>
                              <div style={{
                                height: '100%', width: `${levelPct}%`,
                                background: levelColor, borderRadius: T.radius.full,
                              }} />
                            </div>
                            <span style={{
                              fontSize: '11px', fontWeight: 800,
                              color: levelColor, background: levelBg,
                              padding: '2px 10px', borderRadius: T.radius.full,
                              minWidth: '52px', textAlign: 'center',
                            }}>
                              Lv {s.level}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      </main>
    </div>
  </div>
);

export default CollegeProgress;


